// src/pages/admin/Page_REVI/Page_REVI_MotricityGames.js
//
// M12.1 - Tab "Juegos Motricidad" del panel REVI admin.
//
// Catalogo de juegos VR de motricidad autorizados a enviar telemetria a
// /api/v1/revi/motricity/report. Sustituye a la antigua whitelist
// hardcodeada (constants/reviMotricity.js) por una coleccion Mongo
// gestionable desde aqui. Anadir un juego nuevo = una entrada nueva,
// sin deploy.
//
// Que pueden hacer los admins:
//   - Anadir un juego (gameKey + label + primaryMetric + descripcion).
//   - Editar todos los campos salvo gameKey (intocable: lo referencia el
//     historico de sesiones).
//   - Desactivar (soft delete): el juego deja de aparecer en el linker
//     pero las sesiones historicas se conservan.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  CopyOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { ReviMotricityGamesAPI } from "../../../api";
import {
  CLINICAL_AREAS,
  DEFAULT_CLINICAL_AREA,
} from "../../../Utils/areasClinicas";
import { useAuth } from "../../../hooks";

const { Text, Paragraph } = Typography;

const api = new ReviMotricityGamesAPI();

// Espejo del regex del modelo Mongoose. snake_case puro, 3-40 chars.
const GAME_KEY_REGEX = /^[a-z][a-z0-9_]{2,39}$/;

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export function Page_REVI_MotricityGames() {
  const { accessToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = crear, doc = editar
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!accessToken) return;
      try {
        setLoading(true);
        setError(null);
        const data = await api.list(accessToken);
        if (alive) setItems(data.items || []);
      } catch (e) {
        if (alive) setError(e?.body?.message || e?.message || "Error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, reloadTick]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    // Un alta nueva es v3. El modelo por defecto pone [1] por compatibilidad
    // con los juegos que ya existian, pero una experiencia que se da de alta
    // hoy emite el contrato nuevo: dejar [1] haria que TODOS sus informes
    // rebotaran con "schemaVersion 3 no declarado".
    form.setFieldsValue({
      enabled: true,
      schemaVersions: [3],
      clinicalArea: DEFAULT_CLINICAL_AREA,
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      gameKey: record.gameKey,
      label: record.label,
      enabled: record.enabled,
      supportsLiveStream: record.supportsLiveStream === true,
      primaryMetric: record.primaryMetric || "",
      description: record.description || "",
      schemaVersions: Array.isArray(record.schemaVersions)
        ? record.schemaVersions
        : [1],
      clinicalArea: record.clinicalArea || DEFAULT_CLINICAL_AREA,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        // gameKey no se envia: el server lo ignora aunque se pase.
        const patch = {
          label: values.label,
          enabled: values.enabled,
          supportsLiveStream: values.supportsLiveStream === true,
          primaryMetric: values.primaryMetric || null,
          description: values.description || "",
          schemaVersions: values.schemaVersions,
          clinicalArea: values.clinicalArea || DEFAULT_CLINICAL_AREA,
        };
        await api.update(accessToken, editing.id, patch);
        message.success("Juego actualizado");
      } else {
        await api.create(accessToken, {
          gameKey: values.gameKey,
          label: values.label,
          enabled: values.enabled !== false,
          supportsLiveStream: values.supportsLiveStream === true,
          primaryMetric: values.primaryMetric || null,
          description: values.description || "",
          schemaVersions: values.schemaVersions,
          clinicalArea: values.clinicalArea || DEFAULT_CLINICAL_AREA,
        });
        message.success("Juego creado");
      }
      setModalOpen(false);
      reload();
    } catch (e) {
      if (e?.errorFields) return; // validacion form
      message.error(e?.body?.message || e?.message || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async (record) => {
    try {
      await api.disable(accessToken, record.id);
      message.success("Juego desactivado");
      reload();
    } catch (e) {
      message.error(e?.body?.message || e?.message || "Error desactivando");
    }
  };

  const handleEnableToggle = async (record, nextValue) => {
    try {
      await api.update(accessToken, record.id, { enabled: nextValue });
      message.success(nextValue ? "Juego reactivado" : "Juego desactivado");
      reload();
    } catch (e) {
      message.error(e?.body?.message || e?.message || "Error");
    }
  };

  const handleCopyKey = (key) => {
    try {
      navigator.clipboard.writeText(key);
      message.success(`gameKey "${key}" copiado`);
    } catch (e) {
      message.error("No se pudo copiar");
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "gameKey",
        dataIndex: "gameKey",
        key: "gameKey",
        render: (key) => (
          <Space size={4}>
            <code
              style={{
                background: "#f1f5f9",
                padding: "2px 6px",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              {key}
            </code>
            <Tooltip title="Copiar gameKey">
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                onClick={() => handleCopyKey(key)}
              />
            </Tooltip>
          </Space>
        ),
      },
      {
        title: "Nombre",
        dataIndex: "label",
        key: "label",
      },
      {
        title: "Estado",
        dataIndex: "enabled",
        key: "enabled",
        width: 140,
        render: (enabled, record) => (
          <Switch
            checked={!!enabled}
            checkedChildren="Activo"
            unCheckedChildren="Desactivado"
            onChange={(v) => handleEnableToggle(record, v)}
          />
        ),
      },
      {
        title: (
          <Space size={4}>
            Live
            <Tooltip title="Marca los juegos que han integrado el modulo aframeLiveStream.js y validado el kill-switch FPS. Solo estos pueden activar streaming en directo al terapeuta.">
              <InfoCircleOutlined style={{ color: "#94a3b8" }} />
            </Tooltip>
          </Space>
        ),
        dataIndex: "supportsLiveStream",
        key: "supportsLiveStream",
        width: 110,
        render: (v) =>
          v ? (
            <Tag color="magenta" icon={<VideoCameraOutlined />}>
              Stream
            </Tag>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Text>
          ),
      },
      {
        // Se ve en la tabla para poder detectar de un vistazo un juego que
        // sigue declarando solo v1: es lo que hace rebotar sus informes.
        title: (
          <Space size={4}>
            Contrato
            <Tooltip title="Versiones del JSON que acepta este juego. Las experiencias nuevas emiten la 3.">
              <InfoCircleOutlined style={{ color: "#94a3b8" }} />
            </Tooltip>
          </Space>
        ),
        dataIndex: "schemaVersions",
        key: "schemaVersions",
        width: 120,
        render: (versiones) => (
          <Space size={2}>
            {(Array.isArray(versiones) && versiones.length
              ? versiones
              : [1]
            ).map((v) => (
              <Tag key={v} color={v === 3 ? "green" : "default"}>
                v{v}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        title: "Metrica principal",
        dataIndex: "primaryMetric",
        key: "primaryMetric",
        render: (m) =>
          m ? (
            <Tag color="blue">{m}</Tag>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              (sin asignar)
            </Text>
          ),
      },
      {
        title: "Creado",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 160,
        render: (d) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatDate(d)}
          </Text>
        ),
      },
      {
        title: "",
        key: "actions",
        width: 160,
        render: (_, record) => (
          <Space size={4}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            >
              Editar
            </Button>
            {record.enabled ? (
              <Popconfirm
                title="Desactivar juego"
                description="El historico de sesiones se conserva. Podras reactivarlo en cualquier momento."
                okText="Desactivar"
                okButtonProps={{ danger: true }}
                cancelText="Cancelar"
                onConfirm={() => handleDisable(record)}
              >
                <Tooltip title="Desactivar">
                  <Button
                    size="small"
                    danger
                    icon={<StopOutlined />}
                    aria-label="Desactivar"
                  />
                </Tooltip>
              </Popconfirm>
            ) : null}
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken],
  );

  return (
    <div style={{ padding: "16px 0" }}>
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message="Catalogo de juegos VR autorizados"
        description={
          <Paragraph style={{ marginBottom: 0 }}>
            Cada juego nuevo que envie telemetria a{" "}
            <code>/api/v1/revi/motricity/report</code> debe estar registrado
            aqui con un <code>gameKey</code> snake_case. El equipo del juego usa
            ese <code>gameKey</code> en su JSON; si no coincide o no esta
            activo, el server rechaza con <code>400 gameKey no permitido</code>.
          </Paragraph>
        }
        style={{ marginBottom: 16 }}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <Text strong>{items.length} juegos registrados</Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Añadir juego
        </Button>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <Table
          dataSource={items}
          columns={columns}
          rowKey="id"
          pagination={false}
          size="middle"
        />
      )}

      <Modal
        open={modalOpen}
        title={editing ? "Editar juego" : "Anadir juego"}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        okText={editing ? "Guardar" : "Crear"}
        cancelText="Cancelar"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="gameKey"
            label={
              <Space>
                gameKey
                <Tooltip title="Identificador estable que el juego envia en el JSON. snake_case (a-z, 0-9, _) 3-40 chars y empieza por letra. NO se puede cambiar despues.">
                  <InfoCircleOutlined style={{ color: "#94a3b8" }} />
                </Tooltip>
              </Space>
            }
            rules={[
              { required: true, message: "Requerido" },
              {
                pattern: GAME_KEY_REGEX,
                message:
                  "snake_case 3-40 chars (a-z, 0-9, _), debe empezar por letra",
              },
            ]}
          >
            <Input
              placeholder="motricity_butterfly_vr"
              disabled={!!editing}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            name="label"
            label="Nombre visible"
            rules={[{ required: true, message: "Requerido", whitespace: true }]}
          >
            <Input placeholder="Mariposa VR" maxLength={80} />
          </Form.Item>

          <Form.Item
            name="enabled"
            label="Estado"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren={
                <span>
                  <CheckCircleOutlined /> Activo
                </span>
              }
              unCheckedChildren="Desactivado"
            />
          </Form.Item>

          <Form.Item
            name="supportsLiveStream"
            label={
              <Space>
                Streaming en directo
                <Tooltip title="Solo activar si el juego ha integrado aframeLiveStream.js, validado el kill-switch FPS y aprobado UAT clinico. Con esto OFF, el server rechaza cualquier intento de abrir WS de stream para este gameKey.">
                  <InfoCircleOutlined style={{ color: "#94a3b8" }} />
                </Tooltip>
              </Space>
            }
            valuePropName="checked"
            initialValue={false}
          >
            <Switch
              checkedChildren={
                <span>
                  <VideoCameraOutlined /> Habilitado
                </span>
              }
              unCheckedChildren="No"
            />
          </Form.Item>

          {/*
            El area la declara quien da de alta la experiencia. Para los 9
            juegos legacy manda la tabla de `metricsDeclaration`, donde su area
            esta verificada; para todo lo nuevo —que no esta en esa tabla— este
            campo es la UNICA forma de que la sesion no caiga en "General".
          */}
          <Form.Item
            name="clinicalArea"
            label={
              <Space>
                Area clinica
                <Tooltip title="Agrupa las sesiones de esta experiencia en el panel del terapeuta. Si no lo sabes todavia, deja General: es preferible a asignar un area que nadie ha decidido.">
                  <InfoCircleOutlined style={{ color: "#94a3b8" }} />
                </Tooltip>
              </Space>
            }
          >
            <Select
              options={CLINICAL_AREAS.map((a) => ({ value: a, label: a }))}
            />
          </Form.Item>

          {/*
            LA PUERTA QUE HACIA REBOTAR TODO. El server valida la version del
            contrato JUEGO A JUEGO: si el catalogo dice que este juego emite v1
            y llega un informe v3, se rechaza con "schemaVersion 3 no declarado".

            Este campo no existia en el formulario, asi que todo juego dado de
            alta desde aqui se quedaba con el [1] por defecto del modelo y NINGUN
            informe del contrato nuevo entraba. La guia de experiencias pide
            schemaVersions: [3] y no habia forma de ponerlo sin tocar la base de
            datos a mano.
          */}
          <Form.Item
            name="schemaVersions"
            label={
              <Space>
                Versiones del contrato que acepta
                <Tooltip title="Versiones del JSON que este juego puede enviar. Las experiencias nuevas emiten solo la 3. Un juego legacy que se este migrando puede declarar dos durante la transicion; si aqui no esta la version que envia, sus informes se rechazan.">
                  <InfoCircleOutlined style={{ color: "#94a3b8" }} />
                </Tooltip>
              </Space>
            }
            rules={[
              {
                required: true,
                message: "Declara al menos una version",
                type: "array",
                min: 1,
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Selecciona las versiones"
              options={[
                { value: 3, label: "3 — contrato nuevo (nucleo comun)" },
                { value: 2, label: "2 — legacy con clientReportId" },
                { value: 1, label: "1 — legacy original" },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="primaryMetric"
            label={
              <Space>
                Metrica principal
                <Tooltip title="Clave del objeto summary que la vista generica destaca como KPI grande. Ej: totalFaults, totalScore, accuracy. Opcional.">
                  <InfoCircleOutlined style={{ color: "#94a3b8" }} />
                </Tooltip>
              </Space>
            }
          >
            <Input placeholder="totalFaults" maxLength={60} />
          </Form.Item>

          {/*
            DEJO DE SER OPCIONAL EL 2026-08-11, Y NO ES BUROCRACIA.

            Este texto es lo unico que sabe la plataforma sobre QUE SE HACE en
            la actividad, y va al Sumario Clinico que lee el terapeuta. Con el,
            el sumario escribe "12 errores por contacto"; sin el, solo puede
            decir "12 errores", porque el dato de que son los errores no existe
            en ningun otro sitio. No hay IA que lo supla: si nadie lo ha escrito
            nunca, no esta.

            Cuando se puso opcional, cinco de las once experiencias se quedaron
            sin ella. Rellenarlas despues no arregla el problema de fondo, que
            es que se pueda dar de alta una experiencia incompleta. Obligarlo
            aqui si: cada experiencia nueva entra ya con lo que la plataforma
            necesita para hablar de ella.

            El minimo de 20 caracteres esta para que no valga "juego" ni un
            punto: la frase tiene que decir en que consiste.
          */}
          <Form.Item
            name="description"
            label={
              <Space>
                En que consiste la actividad
                <Tooltip title="Describe QUE HACE el paciente, en una o dos frases. Ej: 'recorrer un alambre sin tocarlo'. Este texto lo usa el Sumario Clinico para decir de que son los errores y los aciertos; sin el, solo puede contarlos.">
                  <InfoCircleOutlined style={{ color: "#94a3b8" }} />
                </Tooltip>
              </Space>
            }
            rules={[
              {
                required: true,
                whitespace: true,
                message:
                  "Sin esto el Sumario Clinico no puede decir que se mide",
              },
              {
                min: 20,
                message: "Describe en que consiste, no solo como se llama",
              },
            ]}
          >
            <Input.TextArea
              rows={3}
              maxLength={500}
              showCount
              placeholder="Recorrer un alambre sin tocarlo con el mando, en fases de dificultad creciente..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
